import { UIIcon } from "@dropbox/dig-icons";
import { CloseLine } from "@dropbox/dig-icons/dist/mjs/assets";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import { PAP_Click_DashChatAction } from "@mirage/analytics/events/types/click_dash_chat_action";
import type { AIToolLibraryConfig } from "@mirage/mosaics/AIToolLibrary/hooks/useAIToolLibrary";
import { ContextInputMessages } from "@mirage/mosaics/Chat/components/chat/ContextInputMessages";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { useTemplateSettingsModal } from "@mirage/mosaics/ComposeAssistant/components/settings/useTemplateSettingsModal";
import { ComposeEditorPane } from "@mirage/mosaics/ComposeAssistant/containers/ComposeEditorPane";
import { useAssistTemplatesContext } from "@mirage/mosaics/ComposeAssistant/data/AssistTemplatesContext";
import type { GetAutoMessagesOnFirstSource } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useAddSources";
import {
  appendManualMessages,
  updateConversationDraft,
} from "@mirage/service-conversation";
import {
  type ConversationRouterState,
  type WriteFromTemplateParams,
  CtaSource,
} from "@mirage/service-conversation/router_state_types";
import type {
  Conversation,
  DocumentInfo,
  ModelId,
  NewUserMessage,
  UserMessageOptions,
} from "@mirage/service-conversation/types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { getDashActionSurfaceComponentLoggingSource } from "@mirage/service-conversation/utils/loggingUtils";
import {
  NoSearch,
  SearchParams,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import type { ComposeAssistantConversationMessage } from "@mirage/shared/compose/assist-message";
import { useIsSmallSizeForSidebar } from "@mirage/shared/responsive/mobile";
import { usePrevious } from "@mirage/shared/util/hooks";
import i18n from "@mirage/translations";
import type { SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";
import { useConversationDraftConfig } from "../../hooks/useConversationDraftConfig";

export function useWriteHandling(
  conversationId: string | undefined,
  conversation: Conversation | undefined,
  isWaitingForResponse: boolean,
  selectedModel: ModelId,
  userMessageOptions: UserMessageOptions,
  setUserMessageOptions: (options: SetStateAction<UserMessageOptions>) => void,
  setManuallyAddedSources: (sources: SetStateAction<ChatSource[]>) => void,
  logComposeEvent: (event: PAPEvent) => void,
  getConversationAttachments: () => Promise<DocumentInfo[]>,
  templateSelectedMessage: ComposeAssistantConversationMessage | undefined,
  onStartNewSessionWithState: (routerState: ConversationRouterState) => void,
  openLibrary: (config?: AIToolLibraryConfig) => void,
) {
  const isSmallSize = useIsSmallSizeForSidebar();
  const [showWritePane, setShowWritePane] = useState(
    !!conversation?.markdownDraft,
  );
  const [isEditorViewOnMobile, setIsEditorViewOnMobile] = useState(false);

  const draftConfig = useConversationDraftConfig({
    conversationId,
    markdownDraft: conversation?.markdownDraft,
    model: selectedModel,
    searchParams: userMessageOptions.searchParams,
    setManuallyAddedSources,
    writeAgentPayload: conversation?.writeAgentPayload,
    setUserMessageOptions,
    logComposeEvent,
  });

  const hasTemplateSelectedMessage = !!templateSelectedMessage;
  const getAutoMessagesOnFirstSource: GetAutoMessagesOnFirstSource =
    useCallback(async () => {
      if (hasTemplateSelectedMessage && conversation?.messages?.length === 1) {
        const source =
          getDashActionSurfaceComponentLoggingSource("compose_chat_pane");
        const isStartFromScratch = draftConfig.selectedTemplate === null;
        const rephrased = isStartFromScratch
          ? ""
          : "Use the template to generate a draft using content from the provided source documents";
        const attachments = await getConversationAttachments();
        const messagePayload: NewUserMessage = {
          message: "",
          rephrased,
          conversationId: conversationId || "",
          agentPayload: JSON.stringify(draftConfig.agentPayload),
          attachments,
          agentIds: ["write"],
          messageType: "userMessage",
          source,
        };
        if (isStartFromScratch) {
          return {
            prependMessages: [
              {
                ...messagePayload,
              },
              {
                conversationId: conversationId || "",
                message: i18n.t("compose_assistant_message_partial_match"),
                llmIgnore: true,
                messageType: "assistantMessage",
                source,
              },
            ],
            newMessage: undefined,
            userMessageOptions: {
              searchParams: new SearchParams({
                searchTarget: {
                  value: new NoSearch(),
                  case: "noSearch",
                  // assume that the first message after start from scratch + attachments won't use search
                },
              }),
              agentIds: ["write"],
            },
          };
        } else {
          return {
            prependMessages: [],
            newMessage: {
              // for any actual template, trigger a request immediately
              ...messagePayload,
              searchParams: new SearchParams({
                searchTarget: {
                  value: new NoSearch(),
                  case: "noSearch",
                },
              }),
            },
            userMessageOptions: undefined,
          };
        }
      }
    }, [
      conversationId,
      hasTemplateSelectedMessage,
      conversation?.messages,
      draftConfig.agentPayload,
      getConversationAttachments,
      draftConfig.selectedTemplate,
    ]);

  const updateDraft = useCallback(
    (markdownContent: string) => {
      if (conversationId) {
        updateConversationDraft(conversationId, markdownContent);
      }
    },
    [conversationId],
  );

  const { templates, saveTemplate, deleteTemplate } =
    useAssistTemplatesContext();

  const prevMarkdownDraft = usePrevious(conversation?.markdownDraft);

  useEffect(() => {
    if (conversation?.markdownDraft && !prevMarkdownDraft) {
      // automatically show the write pane when a draft response comes in
      setShowWritePane(true);
    } else if (!conversation?.markdownDraft) {
      setShowWritePane(false);
    }
  }, [conversation?.markdownDraft, prevMarkdownDraft]);

  const handleTemplateChange = (
    templateId: string | null,
    templateName: string,
  ) => {
    if (
      templateId &&
      (!conversation?.serverConversationId ||
        !userMessageOptions?.agentIds?.includes("write")) &&
      conversation?.messages?.length === 0
    ) {
      // If the user applies a template via the template settings modal and they have not yet engaged
      // with their current session or are mid-conversation in a non-write agent session, start a new
      // client session with write parameters and the selected template applied.
      onStartNewSessionWithState(
        buildConversationRouterState(CtaSource.DraftWithTemplate, {
          templateId,
        } satisfies WriteFromTemplateParams),
      );
    } else {
      // Otherwise, if the user is already in a write agent chat session, we trigger the template change in
      // the standard way, resulting in a rewrite of the current draft.
      draftConfig.onTemplateChange(templateId, templateName);
    }
  };

  const { setOpenTemplateSettings, templateSettingsModal } =
    useTemplateSettingsModal({
      templates: templates || [],
      currentTemplateID: draftConfig.selectedTemplate?.id || null,
      saveTemplate,
      deleteTemplate,
      onTemplateChange: handleTemplateChange,
      postUpdateDraftWithTemplate: draftConfig.onPostMessageWithTemplate,
      logTemplatesPaneEvent: () => {},
    });
  const handleExploreTemplates = useCallback(() => {
    setOpenTemplateSettings({ type: "empty_state" });
    logComposeEvent(
      PAP_Click_DashChatAction({
        featureLine: "assist",
        dashChatAction: "explore_templates",
      }),
    );
  }, [logComposeEvent, setOpenTemplateSettings]);

  const handleToggleMobileView = useCallback(() => {
    setIsEditorViewOnMobile((prev) => !prev);
  }, [setIsEditorViewOnMobile]);

  const onCompleteInputs = useCallback(() => {
    appendManualMessages([
      {
        conversationId: conversationId || "",
        message: i18n.t("compose_assistant_message_partial_match"),
        llmIgnore: true,
        messageType: "assistantMessage",
        source: getDashActionSurfaceComponentLoggingSource("compose_chat_pane"),
      },
    ]);
  }, [conversationId]);
  const getWriteContextInputMessages = useCallback(
    (onClickAddSources: () => void) => {
      return (
        <ContextInputMessages
          sources={[]}
          sourcesContentCache={{}}
          removeSource={() => {}}
          draftConfig={{
            templateID:
              draftConfig.selectedTemplate === null
                ? null
                : draftConfig.selectedTemplate?.id,
            voiceID: draftConfig.selectedVoiceID,
          }}
          setDraftConfig={() => {}}
          onCompleteInputs={onCompleteInputs}
          logComposeEvent={logComposeEvent}
          handleClickAdd={onClickAddSources}
          openLibrary={openLibrary}
          templateProps={{
            templates: templates || [],
            selectedMessage: templateSelectedMessage,
            onTemplateChange: draftConfig.onTemplateChange,
          }}
        />
      );
    },
    [
      draftConfig.onTemplateChange,
      draftConfig.selectedTemplate,
      draftConfig.selectedVoiceID,
      logComposeEvent,
      onCompleteInputs,
      templateSelectedMessage,
      templates,
      openLibrary,
    ],
  );

  const editorPane =
    conversation?.markdownDraft && showWritePane ? (
      <ComposeEditorPane
        markdownDraft={conversation?.markdownDraft}
        isWaitingForResponse={false}
        artifacts={[]}
        setMarkdownContent={updateDraft}
        setDraftConfig={() => {}}
        postUpdateDraftWithVoice={draftConfig.onPostMessageWithVoice}
        messagesHistory={[]}
        currentSessionID={""}
        setInputContext={() => {}}
        postUserMessage={() => {}}
        setOpenTemplateSettings={setOpenTemplateSettings}
        isStreaming={isWaitingForResponse}
        currentVoiceID={draftConfig.selectedVoiceID}
        disableSelectionActions
        assistRequestId={conversation?.assistRequestId}
        openLibrary={openLibrary}
        additionalToolbarButtons={
          isSmallSize &&
          isEditorViewOnMobile && (
            <button
              style={{
                background: "none",
                border: "none",
                height: "24px",
              }}
              onClick={handleToggleMobileView}
              aria-label={i18n.t("assistant_editor_view_toggle")}
            >
              <UIIcon src={CloseLine} />
            </button>
          )
        }
      />
    ) : null;

  return {
    draftConfig,
    getAutoMessagesOnFirstSource,
    templateSettingsModal,
    handleExploreTemplates,
    showWritePane,
    setShowWritePane,
    isEditorViewOnMobile,
    handleToggleMobileView,
    editorPane,
    getWriteContextInputMessages,
  };
}
